import { useState, useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import contactBg from "@/assets/carbonwize-contact-bg.png";
import cimbLogo from "@/assets/logos/cimb-bank-white.png";
import lhBankLogo from "@/assets/logos/lh-bank.png";
import setLogo from "@/assets/logos/set-color.png";
import bridgestoneLogo from "@/assets/logos/bridgestone.png";

const ContactUs = () => {
  const { t, language } = useLanguage();
  const isThai = language === "th";
  const [isLoading, setIsLoading] = useState(true);
  const benefits = [t("contact.benefit1"), t("contact.benefit2"), t("contact.benefit3"), t("contact.benefit4")];

  const logos = [
    { name: "Stock Exchange of Thailand", src: setLogo },
    { name: "CIMB Bank", src: cimbLogo },
    { name: "LH Bank", src: lhBankLogo },
    { name: "Bridgestone", src: bridgestoneLogo },
  ];

  const metadata = {
    en: {
      title: "Contact Us | Carbonwize",
      description: "Get in touch with Carbonwize for a free demo of our carbon management platform. Book a consultation with our sustainability experts.",
      locale: "en_US"
    },
    th: {
      title: "ติดต่อเรา | Carbonwize",
      description: "ติดต่อ Carbonwize เพื่อนัดสาธิตฟรี ปรึกษาผู้เชี่ยวชาญด้านความยั่งยืนของเรา",
      locale: "th_TH"
    }
  };

  const ogTags = {
    title: "Carbonwize | Audit-Grade Carbon Management for Enterprises",
    description: "Automate carbon accounting with enterprise-grade compliance, precision, and automation. Trusted by 500+ enterprises."
  };

  useEffect(() => {
    // Load Pipedrive script dynamically
    const script = document.createElement("script");
    script.src = "https://webforms.pipedrive.com/f/loader";
    script.async = true;
    document.body.appendChild(script);

    // Show loading for 1.5s to allow form to load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return <>
      <SEOHead
        title={metadata[language].title}
        description={metadata[language].description}
        path="/contact-us"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://carbonwize.io"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact Us",
              "item": "https://carbonwize.io/contact-us"
            }
          ]
        }}
      />

      <Navbar />

      <main className="min-h-screen pt-[65px]" style={{
      backgroundColor: "#024241"
    }}>
        {/* Hero Section with Form */}
        <section className="relative overflow-hidden min-h-[calc(100vh-65px)] flex items-center" style={{
        backgroundImage: `url(${contactBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}>
        <div className="container mx-auto px-3 sm:px-4 lg:px-8 py-8 lg:py-12">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-12 items-center">
              {/* Left Content */}
              <div className="relative z-10">
                <h1 className={`font-bold text-white mb-4 ${isThai ? "text-3xl lg:text-4xl" : "text-4xl lg:text-5xl"}`}>
                  {t("contact.title")}
                </h1>

                <p className={`text-white/80 mb-5 max-w-xl ${isThai ? "text-base lg:text-lg" : "text-lg lg:text-xl"}`}>
                  {t("contact.description")}
                </p>

                {/* Benefits */}
                <div className="space-y-3 mb-6">
                  {benefits.map((benefit, index) => <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-white text-lg">{benefit}</span>
                    </div>)}
                </div>

                <p className="text-white text-lg mb-8">{t("contact.formNote")}</p>

                {/* Trusted By Section */}
                <div className="mt-12">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    {t("aboutUs.trustedBy") || "Trusted by 500+ enterprises"}
                  </h3>
                  <div className="relative overflow-hidden">
                    <div className="flex animate-scroll-left items-center">
                      {logos.map((logo, index) => (
                        <div key={`first-${index}`} className="flex-shrink-0 mx-6 sm:mx-8 flex items-center justify-center">
                          <img 
                            src={logo.src} 
                            alt={logo.name} 
                            className="h-4 sm:h-6 w-auto object-contain brightness-0 invert opacity-80"
                          />
                        </div>
                      ))}
                      {logos.map((logo, index) => (
                        <div key={`second-${index}`} className="flex-shrink-0 mx-6 sm:mx-8 flex items-center justify-center">
                          <img 
                            src={logo.src} 
                            alt={logo.name} 
                            className="h-4 sm:h-6 w-auto object-contain brightness-0 invert opacity-80" 
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Contact Form */}
              <div className="relative z-10 flex justify-center lg:justify-start">
                <div className="bg-card rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl border border-border min-h-[400px] relative w-full sm:w-[500px]">
                  {/* Loading overlay - shown on top */}
                  {isLoading && <div className="absolute inset-0 flex flex-col items-center justify-center bg-card rounded-2xl z-10">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                      <p className="text-muted-foreground text-sm mt-4">Loading form...</p>
                    </div>}
                  {/* Pipedrive form - ALWAYS in DOM */}
                  <div className="pipedriveWebForms" data-pd-webforms="https://webforms.pipedrive.com/f/ckoQV8daLsBoBI3iN8xARzWemHXVE8TAUNOtvA6jqFYjKr0Po49qoNHr2xMfKF0LOb" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>;
};
export default ContactUs;